import React, { useState, useEffect, useContext } from 'react';
import { API, AuthContext } from '@/App';
import { CheckCircle, AlertCircle, Clock } from 'lucide-react';

const Readiness = () => {
  const { user } = useContext(AuthContext);
  const [rosters, setRosters] = useState([]);
  const [users, setUsers] = useState([]);
  const [checklistProgress, setChecklistProgress] = useState([]);
  const [stores, setStores] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('token');

  // Total tasks in the pre-stocktake checklist (from PreStocktake.js)
  const TOTAL_CHECKLIST_TASKS = 30; // 5 weeks x 6 tasks per week

  // Get the current store_id
  const getStoreId = () => {
    if (user?.role === 'network_admin' || user?.role === 'regional_manager') {
      return localStorage.getItem('selectedStoreId') || user?.store_id;
    }
    return user?.store_id;
  };

  useEffect(() => {
    fetchData();
    // Refresh data every 30 seconds for live updates
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [user?.store_id]);

  // Listen for store changes
  useEffect(() => {
    const handleStoreChanged = () => fetchData();
    window.addEventListener('storeChanged', handleStoreChanged);
    return () => window.removeEventListener('storeChanged', handleStoreChanged);
  }, []);

  const fetchData = async () => {
    try {
      const storeId = getStoreId();
      const storeParam = storeId ? `?store_id=${storeId}` : '';
      
      const [rostersRes, usersRes, storesRes, devicesRes] = await Promise.all([
        fetch(`${API}/rosters${storeParam}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/users${storeParam}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/stores`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/devices${storeParam}`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      // Fetch checklist progress separately to handle potential errors
      let checklistData = [];
      try {
        const checklistRes = await fetch(`${API}/weekly-checklist-progress${storeParam}`, { 
          headers: { Authorization: `Bearer ${token}` } 
        });
        if (checklistRes.ok) {
          checklistData = await checklistRes.json();
        }
      } catch (e) {
        console.log('Checklist progress not available');
      }
      
      setRosters(rostersRes.ok ? await rostersRes.json() : []);
      setUsers(usersRes.ok ? await usersRes.json() : []);
      setStores(storesRes.ok ? await storesRes.json() : []);
      setDevices(devicesRes.ok ? await devicesRes.json() : []);
      setChecklistProgress(checklistData);
    } catch (error) {
      console.error('Error fetching readiness data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate roster stats - Red if <5, Yellow if 5-9, Green if 10+
  const getRosterStats = () => {
    const uniqueRosteredMembers = new Set(rosters.map(r => r.user_name || r.user_id)).size;
    let status;
    if (uniqueRosteredMembers >= 10) {
      status = 'complete'; // Green
    } else if (uniqueRosteredMembers >= 5) {
      status = 'in-progress'; // Yellow
    } else {
      status = 'warning'; // Red
    }
    return {
      assigned: uniqueRosteredMembers,
      status
    };
  };

  // Calculate checklist progress - Red if 0%, Yellow if 1-99%, Green if 100%
  const getChecklistStats = () => {
    // Count completed tasks from the checklist progress
    let completedTasks = 0;
    if (Array.isArray(checklistProgress)) {
      checklistProgress.forEach(item => {
        if (item.completed_tasks) {
          completedTasks += Object.keys(item.completed_tasks).length;
        }
      });
    }
    
    const percentage = TOTAL_CHECKLIST_TASKS > 0 
      ? Math.round((completedTasks / TOTAL_CHECKLIST_TASKS) * 100) 
      : 0;
    
    let status;
    if (percentage === 100) {
      status = 'complete'; // Green
    } else if (percentage >= 1) {
      status = 'in-progress'; // Yellow
    } else {
      status = 'warning'; // Red - 0%
    }
    
    return {
      completed: completedTasks,
      total: TOTAL_CHECKLIST_TASKS,
      percentage,
      status
    };
  };

  // Calculate store setup status
  const getStoreStatus = () => {
    const hasStores = stores.length > 0;
    return {
      status: hasStores ? 'complete' : 'pending',
      description: hasStores ? 'Store information and areas configured' : 'Store setup required'
    };
  };

  // Calculate device allocation status - Yellow by default, Green when 4+ devices
  const getDeviceStatus = () => {
    const totalDevices = devices.length;
    
    if (totalDevices >= 4) {
      return {
        status: 'complete', // Green
        description: `${totalDevices} devices allocated`
      };
    } else if (totalDevices > 0) {
      return {
        status: 'in-progress', // Yellow
        description: `${totalDevices} device${totalDevices !== 1 ? 's' : ''} allocated (minimum 4 required)`
      };
    } else {
      return {
        status: 'in-progress', // Yellow
        description: 'No devices allocated yet'
      };
    }
  };

  const rosterStats = getRosterStats();
  const checklistStats = getChecklistStats();
  const storeStatus = getStoreStatus();
  const deviceStatus = getDeviceStatus();

  const readinessChecks = [
    { 
      title: 'Store Setup Complete', 
      status: storeStatus.status, 
      description: storeStatus.description 
    },
    { 
      title: 'Team Roster Assigned', 
      status: rosterStats.status, 
      description: `${rosterStats.assigned} team member${rosterStats.assigned !== 1 ? 's' : ''} assigned` 
    },
    { 
      title: 'Devices Allocated', 
      status: deviceStatus.status, 
      description: deviceStatus.description 
    },
    { 
      title: 'Pre-Stocktake Checklist', 
      status: checklistStats.status, 
      description: `${checklistStats.percentage}% complete (${checklistStats.completed}/${checklistStats.total} tasks)` 
    }
  ];

  const getStatusIcon = (status) => {
    switch (status) {
      case 'complete':
        return <CheckCircle className="w-6 h-6 text-green-600" />;
      case 'in-progress':
        return <Clock className="w-6 h-6 text-orange-500" />;
      case 'warning':
        return <AlertCircle className="w-6 h-6 text-red-500" />;
      default:
        return <AlertCircle className="w-6 h-6 text-slate-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'complete':
        return 'border-green-200 bg-green-50';
      case 'in-progress':
        return 'border-orange-200 bg-orange-50';
      case 'warning':
        return 'border-red-200 bg-red-50';
      default:
        return 'border-slate-200 bg-white';
    }
  };

  // Calculate overall readiness percentage
  const getOverallReadiness = () => {
    const completed = readinessChecks.filter(c => c.status === 'complete').length;
    return Math.round((completed / readinessChecks.length) * 100);
  };

  if (loading) {
    return (
      <div className="space-y-8" data-testid="readiness-page">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 tracking-tight mb-2">Readiness</h1>
          <p className="text-slate-600">Track your stocktake preparation status</p>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="readiness-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 tracking-tight mb-2">Readiness</h1>
          <p className="text-slate-600">Track your stocktake preparation status</p>
        </div>
        <div className="text-right">
          <div className="text-3xl font-bold text-teal-600">{getOverallReadiness()}%</div>
          <p className="text-sm text-slate-500">Overall Ready</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {readinessChecks.map((check, index) => (
          <div
            key={index}
            className={`rounded-xl border-2 p-6 transition-all hover:shadow-md ${getStatusColor(check.status)}`}
            data-testid={`readiness-check-${index}`}
          >
            <div className="flex items-start gap-4">
              <div className="mt-1">{getStatusIcon(check.status)}</div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-slate-900 mb-1">{check.title}</h3>
                <p className="text-sm text-slate-600">{check.description}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Progress Summary */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">Progress Overview</h3>
        <div className="space-y-4">
          {/* Roster Count - Red if <5, Yellow if 5-9, Green if 10+ */}
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-slate-600">Team Roster</span>
              <span className="font-medium text-slate-900">{rosterStats.assigned} assigned</span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div 
                className={`h-full rounded-full transition-all duration-500 ${
                  rosterStats.assigned >= 10 ? 'bg-green-500' : 
                  rosterStats.assigned >= 5 ? 'bg-amber-500' : 
                  'bg-red-500'
                }`}
                style={{ width: rosterStats.assigned > 0 ? `${Math.min((rosterStats.assigned / 10) * 100, 100)}%` : '5%' }}
              />
            </div>
          </div>
          
          {/* Checklist Progress Bar - Red if 0%, Yellow if 1-99%, Green if 100% */}
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-slate-600">Pre-Stocktake Checklist</span>
              <span className="font-medium text-slate-900">{checklistStats.percentage}%</span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div 
                className={`h-full rounded-full transition-all duration-500 ${
                  checklistStats.percentage === 100 ? 'bg-green-500' : 
                  checklistStats.percentage >= 1 ? 'bg-amber-500' : 
                  'bg-red-500'
                }`}
                style={{ width: checklistStats.percentage > 0 ? `${checklistStats.percentage}%` : '5%' }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Readiness;
